import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LogOut, Menu } from "lucide-react";
import Lantern from "./Lantern";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const links = user
    ? [
        { to: "/dashboard", label: "Dashboard" },
        { to: "/send", label: "Send Money" },
        { to: "/recipients", label: "Recipients" },
        { to: "/history", label: "History" },
        { to: "/kyc", label: "Verification" },
      ]
    : [
        { to: "/", label: "Home" },
        { to: "/send", label: "Send Money" },
        { to: "/rates", label: "Rates and Fees" },
      ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-[var(--ra-border)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 group" data-testid="brand-link">
          <div className="relative w-9 h-9 rounded-lg bg-[var(--ra-primary)] flex items-center justify-center transition-shadow group-hover:shadow-[0_0_22px_-4px_rgba(251,191,36,0.55)]">
            <Lantern size={18} className="text-white" glow />
          </div>
          <div className="leading-tight">
            <span className="font-heading font-bold text-lg tracking-tight block">ClearPurse</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--ra-muted)] hidden sm:block">The clear way home</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                loc.pathname === l.to
                  ? "text-[var(--ra-primary)] font-semibold"
                  : "text-[var(--ra-muted)] hover:text-[var(--ra-primary)]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="rounded-full border-[var(--ra-border)]">
                  <div className="w-6 h-6 rounded-full bg-[var(--ra-primary)] text-white text-xs flex items-center justify-center font-semibold">
                    {user.full_name?.[0]?.toUpperCase()}
                  </div>
                  <span className="ml-2 hidden sm:inline">{user.full_name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user.full_name}</div>
                  <div className="text-xs text-[var(--ra-muted)]">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/kyc")} data-testid="menu-kyc">
                  Verification
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login-btn" className="text-[var(--ra-primary)]">Login</Button>
              <Button size="sm" className="btn-primary rounded-md" onClick={() => nav("/register")} data-testid="nav-signup-btn">Get Started</Button>
            </>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu size={20} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {links.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => nav(l.to)}>{l.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
